import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';
import { Users, CreditCard, Shield, Calendar, Search, Download, Zap, Crown } from 'lucide-react';
import ProfilePicture from '@/components/ProfilePicture';
import { Pagination } from '@/components/Pagination';
import MemberPlanModal from '@/components/admin/MemberPlanModal';
import { ViewAsButton } from '@/components/admin/ViewAsButton';
import { SupportHistoryLine } from '@/components/admin/SupportHistoryLine';
import { ChangeEmailDialog, RemoveAccountDialog } from '@/components/admin/AccountTools';

const AdminMembershipManagement = ({ user }) => {
  const [users, setUsers] = useState([]);
  const [payments, setPayments] = useState([]);
  const [paymentsPage, setPaymentsPage] = useState(1);
  const [paymentsTotalPages, setPaymentsTotalPages] = useState(1);
  const [paymentsTotal, setPaymentsTotal] = useState(0);
  const PAYMENTS_PER_PAGE = 50;
  const [showPaymentsHistory, setShowPaymentsHistory] = useState(false);
  const [dripPurchases, setDripPurchases] = useState([]);
  const [dripPage, setDripPage] = useState(1);
  const [dripTotalPages, setDripTotalPages] = useState(1);
  const [dripTotal, setDripTotal] = useState(0);
  const [dripRevenue, setDripRevenue] = useState(0);
  const [showDripPurchases, setShowDripPurchases] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [changingEmail, setChangingEmail] = useState(null);
  const [removingAccount, setRemovingAccount] = useState(null);
  const [planMember, setPlanMember] = useState(null);
  const [userPayments, setUserPayments] = useState(null);
  const [userAcceptances, setUserAcceptances] = useState(null);
  const [userProgress, setUserProgress] = useState({});
  // One call for every card: who has an open or pending grant, and when each was last helped.
  const [viewAsSummary, setViewAsSummary] = useState({ live: {}, last_helped: {} });
  const [loading, setLoading] = useState(true);
  const [showArchived, setShowArchived] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const LIMIT = 50;

  // CSV export
  const [showExport, setShowExport] = useState(false);
  const [exportTier, setExportTier] = useState('');
  const [availableFields, setAvailableFields] = useState([]);
  const [selectedCols, setSelectedCols] = useState([
    'full_name', 'email', 'username', 'membership_tier', 'role', 'status', 'created_at', 'is_vendor',
  ]);
  const [exporting, setExporting] = useState(false);

  const openExportDialog = async () => {
    setShowExport(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/admin/users/export/fields`, { headers: { Authorization: `Bearer ${token}` } });
      setAvailableFields(res.data?.fields || []);
    } catch (e) {
      setAvailableFields([]);
    }
  };

  const toggleCol = (field) => {
    setSelectedCols((prev) => prev.includes(field) ? prev.filter((c) => c !== field) : [...prev, field]);
  };

  const doExport = async () => {
    if (selectedCols.length === 0) { toast.error('Select at least one column'); return; }
    setExporting(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (exportTier) params.set('tier', exportTier);
      params.set('columns', selectedCols.join(','));
      const res = await axios.get(`${API}/admin/users/export?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `members_export_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success('Export downloaded');
      setShowExport(false);
    } catch (e) {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchMemberStats();
    fetchViewAsSummary();
  }, [currentPage, searchQuery]);

  useEffect(() => {
    fetchPayments();
  }, [paymentsPage]);

  useEffect(() => {
    fetchDripPurchases();
  }, [dripPage]);

  const fetchViewAsSummary = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/view-as/summary`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setViewAsSummary(res.data || { live: {}, last_helped: {} });
    } catch {
      setViewAsSummary({ live: {}, last_helped: {} });
    }
  };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        page: currentPage,
        limit: LIMIT,
        ...(searchQuery && { search: searchQuery }),
      });
      const response = await axios.get(`${API}/admin/users?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = response.data;
      setUsers(data.users || data);
      setTotalPages(data.total_pages || 1);
      setTotalUsers(data.total || 0);
      
      // Fetch progress for displayed users
      fetchAllUserProgress(data.users || data, token);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllUserProgress = async (usersList, token) => {
    try {
      const progressData = {};
      
      // Fetch progress for each user
      await Promise.all(
        usersList.map(async (u) => {
          try {
            const response = await axios.get(`${API}/admin/users/${u.id}/progress`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            progressData[u.id] = response.data;
          } catch (error) {
            console.error(`Failed to load progress for user ${u.id}`);
            progressData[u.id] = null;
          }
        })
      );
      
      setUserProgress(progressData);
    } catch (error) {
      console.error('Failed to load user progress');
    }
  };

  const fetchPayments = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/admin/payments/history?page=${paymentsPage}&per_page=${PAYMENTS_PER_PAGE}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPayments(response.data?.payments || []);
      setPaymentsTotalPages(response.data?.total_pages || 1);
      setPaymentsTotal(response.data?.total || 0);
    } catch (error) {
      console.error('Failed to load payments');
    }
  };

  const fetchDripPurchases = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/admin/drip-removal-purchases?page=${dripPage}&per_page=25`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDripPurchases(response.data?.purchases || []);
      setDripTotalPages(response.data?.total_pages || 1);
      setDripTotal(response.data?.total || 0);
      setDripRevenue(response.data?.total_revenue_cents || 0);
    } catch (error) {
      console.error('Failed to load drip removal purchases');
    }
  };

  // Global member-stat counts (Session 31 Task 3 — was previously computed
  // from the paginated `users` array which only reflected the current page).
  const [memberStats, setMemberStats] = useState({
    total_users: 0, active_members: 0, free_users: 0, admins: 0, banned: 0, archived: 0,
  });

  const fetchMemberStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/users/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMemberStats(response.data || {});
    } catch (error) {
      console.error('Failed to load member stats', error);
    }
  };

  const fetchUserPayments = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/users/${userId}/payments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserPayments(response.data);
    } catch (error) {
      toast.error('Failed to load user payments');
    }
  };

  const fetchUserAcceptances = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/users/${userId}/acceptances`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserAcceptances(response.data);
    } catch (error) {
      console.error('Failed to load user acceptances');
    }
  };

  const [userDetails, setUserDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);

  const fetchUserDetails = async (userId) => {
    setDetailsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/users/${userId}/details`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserDetails(response.data);
    } catch (error) {
      console.error('Failed to load user details');
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleGrantMembership = async (userId, membershipType, durationDays) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/users/grant-membership`, {
        user_id: userId,
        membership_type: membershipType,
        duration_days: durationDays
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Membership granted successfully!');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to grant membership');
    }
  };

  const handleGrantFreeMembership = async (userId) => {
    if (!window.confirm('Grant FREE (comp) membership with full access? This will NOT count as a paid membership.')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/users/grant-free-membership`, {
        user_id: userId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Free membership granted successfully!');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to grant free membership');
    }
  };

  const handleRevokeMembership = async (userId) => {
    if (!window.confirm('⚠️ REVOKE MEMBERSHIP?\n\nThis will:\n• Remove ALL paid/comp access\n• Return user to FREE status\n• User will only have access to Module 1\n\nAre you sure?')) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/admin/users/revoke-membership`, {
        user_id: userId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message || 'Membership revoked - user has Module 1 access only');
      fetchUsers();
      if (selectedUser && selectedUser.id === userId) {
        setSelectedUser(null);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to revoke membership');
    }
  };

  const handleToggleAdmin = async (userId, currentRole) => {
    const makeAdmin = currentRole !== 'admin';
    if (!window.confirm(`Are you sure you want to ${makeAdmin ? 'promote' : 'demote'} this user?`)) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/users/assign-admin`, {
        user_id: userId,
        make_admin: makeAdmin
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`User ${makeAdmin ? 'promoted to admin' : 'demoted to user'} successfully`);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update role');
    }
  };

  const handleBanUser = async (userId) => {
    const reason = prompt('⛔ BAN USER\n\nEnter reason for banning this user:', 'Violation of terms of service');
    if (!reason) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/users/ban`, {
        user_id: userId,
        reason: reason
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('User banned successfully - they cannot login anymore');
      fetchUsers();
      if (selectedUser && selectedUser.id === userId) {
        setSelectedUser(null);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to ban user');
    }
  };

  const handleUnbanUser = async (userId) => {
    if (!window.confirm('Unban this user and restore their login access?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/users/unban`, {
        user_id: userId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('User unbanned - they can login again');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unban user');
    }
  };

  const handleArchiveUser = async (userId) => {
    const reasons = ['inactive', 'kicked_out', 'spam', 'other'];
    const reasonChoice = prompt(
      '📁 ARCHIVE USER\n\nSelect reason:\n1. inactive\n2. kicked_out\n3. spam\n4. other (will prompt for details)\n\nEnter 1-4:',
      '1'
    );
    
    if (!reasonChoice) return;
    
    let reason = reasons[parseInt(reasonChoice) - 1] || 'inactive';
    if (reason === 'other') {
      reason = prompt('Enter custom reason:', 'Violation of guidelines');
      if (!reason) return;
    }
    
    if (!window.confirm(`📁 Archive user?\n\nReason: ${reason}\n\nUser will be:\n• Moved to Archived section\n• Unable to login\n• Auto-deleted after 7 years\n• Payment records preserved\n\nContinue?`)) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/admin/users/archive`, {
        user_id: userId,
        reason: reason
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`User archived - Auto-delete date: ${new Date(response.data.auto_delete_date).toLocaleDateString()}`);
      fetchUsers();
      if (selectedUser && selectedUser.id === userId) {
        setSelectedUser(null);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to archive user');
    }
  };

  const handleDeleteUser = async (userId, username) => {
    if (!window.confirm(`⚠️ PERMANENT DELETE\n\nUser: ${username}\n\nThis will PERMANENTLY delete:\n• User profile\n• Progress data\n• Forum posts\n\nPreserved (legal):\n• Payment records\n• Legal acceptances\n\nTHIS CANNOT BE UNDONE!\n\nType "DELETE" below to confirm:`)) return;
    
    const confirmation = prompt('Type DELETE to confirm permanent deletion:');
    if (confirmation !== 'DELETE') {
      toast.error('Deletion cancelled - confirmation text did not match');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/admin/users/delete`, {
        user_id: userId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message + ' - Payment/legal records preserved');
      fetchUsers();
      if (selectedUser && selectedUser.id === userId) {
        setSelectedUser(null);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleUnarchiveUser = async (userId) => {
    if (!window.confirm('Restore this user from archive?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/users/unarchive`, {
        user_id: userId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('User restored from archive');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unarchive user');
    }
  };

  const titleCase = (v) => (v ? v.charAt(0).toUpperCase() + v.slice(1) : '');

  const getMembershipBadge = (membershipType, paymentStatus, membershipTier) => {
    if (membershipType === 'free') {
      return <Badge variant="outline" className="text-gray-500">Free</Badge>;
    }
    if (membershipType === 'free_comp') {
      return <Badge className="bg-purple-500 text-white">Free (Comp)</Badge>;
    }
    if (paymentStatus === 'active') {
      const tier = titleCase(membershipTier);
      const period = titleCase(membershipType);
      return (
        <Badge className="bg-green-500 text-white">
          {tier ? `${tier} · ${period}` : period}
        </Badge>
      );
    }
    return <Badge className="bg-red-500 text-white">Expired</Badge>;
  };

  if (loading) {
    return <div className="text-center py-20">Loading...</div>;
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {showExport && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4" data-testid="export-dialog"
          onClick={() => setShowExport(false)}>
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-xl font-bold text-gray-900 mb-4">Export Members to CSV</h3>
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-1">Filter by tier</label>
              <select
                value={exportTier}
                onChange={(e) => setExportTier(e.target.value)}
                className="w-full p-2 border rounded-md bg-white text-black"
                data-testid="export-tier-select"
              >
                <option value="">All tiers</option>
                <option value="free">Free</option>
                <option value="gold">Gold</option>
                <option value="platinum">Platinum</option>
                <option value="platinum_plus">Platinum+</option>
              </select>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Columns to include</label>
              <div className="grid grid-cols-2 gap-1 max-h-56 overflow-y-auto border rounded-md p-3">
                {(availableFields.length ? availableFields : selectedCols).map((f) => (
                  <label key={f} className="flex items-center gap-2 text-sm text-gray-800">
                    <input
                      type="checkbox"
                      checked={selectedCols.includes(f)}
                      onChange={() => toggleCol(f)}
                      data-testid={`export-col-${f}`}
                    />
                    {f}
                  </label>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-1">Tick any field to add it as a column. "is_vendor" flags marketplace vendors.</p>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowExport(false)}>Cancel</Button>
              <Button onClick={doExport} disabled={exporting} data-testid="export-download-btn">
                <Download className="w-4 h-4 mr-2" /> {exporting ? 'Exporting…' : 'Download CSV'}
              </Button>
            </div>
          </div>
        </div>
      )}
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-white mb-3">Membership Management</h1>
        <p className="text-lg text-gray-300">Manage users, memberships, and permissions</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <Card className="glass-effect border-cyan-500/30">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm" style={{ color: '#331a67' }}>Total Users</p>
                <p className="text-3xl font-bold" style={{ color: '#331a67' }}>
                  {totalUsers}
                </p>
              </div>
              <Users className="w-10 h-10" style={{ color: '#331a67' }} />
            </div>
          </CardContent>
        </Card>

        <Card className="glass-effect border-cyan-500/30">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm" style={{ color: '#331a67' }}>Active Members</p>
                <p className="text-3xl font-bold" style={{ color: '#331a67' }} data-testid="stat-active-members">
                  {memberStats.active_members}
                </p>
              </div>
              <CreditCard className="w-10 h-10" style={{ color: '#331a67' }} />
            </div>
          </CardContent>
        </Card>

        <Card className="glass-effect border-cyan-500/30">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm" style={{ color: '#331a67' }}>Free Users</p>
                <p className="text-3xl font-bold" style={{ color: '#331a67' }} data-testid="stat-free-users">
                  {memberStats.free_users}
                </p>
              </div>
              <Users className="w-10 h-10" style={{ color: '#331a67' }} />
            </div>
          </CardContent>
        </Card>

        <Card className="glass-effect border-cyan-500/30">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm" style={{ color: '#331a67' }}>Admins</p>
                <p className="text-3xl font-bold" style={{ color: '#331a67' }} data-testid="stat-admins">
                  {memberStats.admins}
                </p>
              </div>
              <Shield className="w-10 h-10" style={{ color: '#331a67' }} />
            </div>
          </CardContent>
        </Card>

        <Card className="glass-effect border-cyan-500/30">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm" style={{ color: '#331a67' }}>Banned</p>
                <p className="text-3xl font-bold text-red-600" data-testid="stat-banned">
                  {memberStats.banned}
                </p>
              </div>
              <div className="w-10 h-10 flex items-center justify-center text-red-600 text-2xl">
                ⛔
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="glass-effect border-cyan-500/30">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm" style={{ color: '#331a67' }}>Archived</p>
                <p className="text-3xl font-bold text-orange-600" data-testid="stat-archived">
                  {memberStats.archived}
                </p>
              </div>
              <div className="w-10 h-10 flex items-center justify-center text-orange-600 text-2xl">
                📁
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Users Table */}
      <Card className="glass-effect border-cyan-500/30">
        <CardHeader>
          <div className="flex items-center justify-between gap-4">
            <CardTitle className="font-bold" style={{ color: '#331a67 !important', fontFamily: "'Cormorant Garamond', serif" }}>All Users</CardTitle>
            <div className="flex-1 max-w-md relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                type="text"
                placeholder="Search by name or email..."
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
                className="w-full pl-10"
                data-testid="members-search-input"
              />
            </div>
            <span className="text-sm text-gray-400">
              Page {currentPage} of {totalPages} ({totalUsers} users)
            </span>
            <Button onClick={openExportDialog} variant="outline" size="sm" className="gap-2" data-testid="export-users-btn">
              <Download className="w-4 h-4" /> Export CSV
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Users List - Card Layout */}
          <div className="grid grid-cols-1 gap-4">
            {users
              .filter(u => !u.is_archived)
              .map((u) => (
              <Card key={u.id} className="border border-gray-700 hover:border-cyan-500/50 transition-all">
                <CardContent className="p-4">
                  {/* User Info Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-7 gap-4 mb-4">
                    {/* Profile Picture & Name & Email */}
                    <div className="md:col-span-2 flex items-start gap-3">
                      <ProfilePicture
                        src={u.profile_picture}
                        alt={u.full_name}
                        size="md"
                      />
                      <div>
                        <p className="font-semibold" style={{ color: '#445277' }}>{u.full_name}</p>
                        <p className="text-sm" style={{ color: '#331a67' }}>{u.email}</p>
                        {u.platinum_plus && (
                          <p className="text-xs mt-1 inline-flex items-center gap-1 rounded-full px-2 py-0.5"
                             style={{ color: '#5C0A4E', background: 'rgba(127,13,108,0.10)', border: '1px solid rgba(127,13,108,0.3)' }}
                             data-testid={`member-platinum-plus-${u.id}`}>
                            <Crown className="w-3 h-3" /> Crossed into Platinum+
                          </p>
                        )}
                      </div>
                    </div>
                    
                    {/* Membership Status */}
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Membership</p>
                      {getMembershipBadge(u.membership_type, u.payment_status, u.membership_tier)}
                      <p className="text-xs mt-1" style={{ color: '#331a67' }}>
                        {u.membership_expires_at 
                          ? `Expires: ${new Date(u.membership_expires_at).toLocaleDateString()}`
                          : 'No expiry'
                        }
                      </p>
                    </div>
                    
                    {/* Progress */}
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Progress</p>
                      {userProgress[u.id]?.progress ? (
                        <div className="flex flex-col gap-1">
                          <div className="text-xs" style={{ color: '#331a67' }}>
                            {userProgress[u.id].progress.completed_modules}/{userProgress[u.id].progress.total_modules} modules
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div 
                              className="bg-cyan-600 h-2 rounded-full" 
                              style={{ width: `${userProgress[u.id].progress.percentage}%` }}
                            />
                          </div>
                          <div className="text-xs text-gray-500">
                            {userProgress[u.id].progress.percentage}%
                          </div>
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400">Loading...</span>
                      )}
                    </div>
                    
                    {/* Current Module */}
                    <div className="md:col-span-2">
                      <p className="text-xs text-gray-500 mb-1">Current Module</p>
                      {userProgress[u.id]?.current_module ? (
                        <div className="flex flex-col gap-1">
                          <span className="text-sm font-medium" style={{ color: '#331a67' }}>
                            {userProgress[u.id].current_module.title}
                          </span>
                          <Badge 
                            variant="outline" 
                            className={`text-xs w-fit ${
                              userProgress[u.id].current_module.status === 'completed' ? 'bg-green-100 text-green-700' :
                              userProgress[u.id].current_module.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                              'bg-gray-100 text-gray-700'
                            }`}
                          >
                            {userProgress[u.id].current_module.status.replace('_', ' ')}
                          </Badge>
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400">No modules</span>
                      )}
                    </div>
                    
                    {/* Role & Status */}
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Role</p>
                      <div className="flex flex-col gap-1">
                        <Badge variant={u.role === 'admin' ? 'default' : 'outline'}>
                          {u.role}
                        </Badge>
                        {u.is_banned && (
                          <Badge className="bg-red-600 text-white text-xs">
                            BANNED
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Current Content - Full Width Row */}
                  <div className="mb-4 pb-3 border-b border-gray-200">
                    <p className="text-xs text-gray-500 mb-1">Current Content</p>
                    {userProgress[u.id]?.current_content ? (
                      <div className="flex flex-col gap-1">
                        <span className="text-sm" style={{ color: '#445277' }}>
                          {userProgress[u.id].current_content.title}
                        </span>
                        <div className="flex gap-1">
                          <Badge variant="outline" className="text-xs capitalize">
                            {userProgress[u.id].current_content.content_type}
                          </Badge>
                          <Badge 
                            variant="outline" 
                            className={`text-xs ${
                              userProgress[u.id].current_content.status === 'completed' ? 'bg-green-100 text-green-700' :
                              userProgress[u.id].current_content.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                              'bg-gray-100 text-gray-700'
                            }`}
                          >
                            {userProgress[u.id].current_content.status.replace('_', ' ')}
                          </Badge>
                        </div>
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400">No content</span>
                    )}
                  </div>
                  
                  <SupportHistoryLine entry={viewAsSummary.last_helped?.[u.id]} memberId={u.id} />

                  {/* Action Buttons - Spread across bottom */}
                  <div className="flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setSelectedUser(u);
                        setUserDetails(null);
                        fetchUserPayments(u.id);
                        fetchUserAcceptances(u.id);
                        fetchUserDetails(u.id);
                      }}
                      className="text-xs"
                      data-testid={`view-member-${u.id}`}
                    >
                      View
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setPlanMember(u)}
                      className="text-xs"
                      title="Payment plan, instalments and maintenance fee"
                      data-testid={`plan-member-${u.id}`}
                    >
                      Plan
                    </Button>
                    <ViewAsButton
                      member={u}
                      live={viewAsSummary.live?.[u.id]}
                      onChanged={fetchViewAsSummary}
                    />
                    {u.membership_type !== 'free' && (
                      <Button
                        size="sm"
                        variant="destructive"
                        className="text-xs"
                        onClick={() => handleRevokeMembership(u.id)}
                        title="Revoke membership - return to free status (Module 1 only)"
                      >
                        Kick Out
                      </Button>
                    )}
                    {u.id !== user.id && !u.is_banned && (
                      <Button
                        size="sm"
                        className="bg-red-600 hover:bg-red-700 text-white text-xs"
                        onClick={() => handleBanUser(u.id)}
                        title="Ban user - they cannot login"
                      >
                        Ban
                      </Button>
                    )}
                    {u.is_banned && (
                      <Button
                        size="sm"
                        className="bg-green-600 hover:bg-green-700 text-white text-xs"
                        onClick={() => handleUnbanUser(u.id)}
                        title="Unban user - restore login access"
                      >
                        Unban
                      </Button>
                    )}
                    {u.id !== user.id && (
                      <Button
                        size="sm"
                        className="bg-orange-600 hover:bg-orange-700 text-white text-xs"
                        onClick={() => handleArchiveUser(u.id)}
                        title="Archive user - soft delete with 7-year retention"
                      >
                        Archive
                      </Button>
                    )}
                    <Button
                      size="sm"
                      className="bg-cyan-700 hover:bg-cyan-600 text-white text-xs"
                      onClick={() => setChangingEmail({
                        id: u.id, email: u.email, full_name: u.full_name,
                        signs_in_with_google: !!u.google_id,
                      })}
                      title="Change the address this person signs in with"
                      data-testid={`change-email-btn-${u.id}`}
                    >
                      Change email
                    </Button>
                    {u.id !== user.id && u.role !== 'admin' && (
                      <Button
                        size="sm"
                        className="bg-rose-700 hover:bg-rose-600 text-white text-xs"
                        onClick={() => setRemovingAccount({
                          id: u.id, email: u.email, full_name: u.full_name,
                        })}
                        title="Delete this account outright — refuses if it owns anything"
                        data-testid={`remove-account-btn-${u.id}`}
                      >
                        Delete account
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-6 pt-4 border-t border-gray-700">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage <= 1}
                data-testid="members-prev-page"
              >
                Previous
              </Button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const start = Math.max(1, Math.min(currentPage - 2, totalPages - 4));
                const pageNum = start + i;
                if (pageNum > totalPages) return null;
                return (
                  <Button
                    key={pageNum}
                    size="sm"
                    variant={pageNum === currentPage ? 'default' : 'outline'}
                    onClick={() => setCurrentPage(pageNum)}
                    className={pageNum === currentPage ? 'bg-teal-600' : ''}
                  >
                    {pageNum}
                  </Button>
                );
              })}
              <Button
                size="sm"
                variant="outline"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage >= totalPages}
                data-testid="members-next-page"
              >
                Next
              </Button>
            </div>
          )}
        </CardContent>
      </Card>


      {/* Archived Users Section */}
      <Card className="glass-effect border-orange-500/30">
        <CardHeader>
          <button
            onClick={() => setShowArchived(!showArchived)}
            className="w-full flex items-center justify-between hover:opacity-80 transition-opacity"
          >
            <div className="flex items-center gap-3">
              <span style={{ color: '#331a67', fontFamily: "'Cormorant Garamond', serif" }} className="text-xl font-bold">
                📁 Archived Users ({users.filter(u => u.is_archived).length})
              </span>
              <span className="text-sm text-gray-500">7-year retention before auto-delete</span>
            </div>
            <span className="text-2xl">{showArchived ? '▼' : '▶'}</span>
          </button>
        </CardHeader>
        {showArchived && (
          <CardContent>
            {users.filter(u => u.is_archived).length === 0 ? (
              <p className="text-gray-500 text-center py-4">No archived users</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-700">
                      <th className="text-left py-3 px-4" style={{ color: '#331a67' }}>Name</th>
                      <th className="text-left py-3 px-4" style={{ color: '#331a67' }}>Email</th>
                      <th className="text-left py-3 px-4" style={{ color: '#331a67' }}>Archived Date</th>
                      <th className="text-left py-3 px-4" style={{ color: '#331a67' }}>Reason</th>
                      <th className="text-left py-3 px-4" style={{ color: '#331a67' }}>Auto-Delete Date</th>
                      <th className="text-left py-3 px-4" style={{ color: '#331a67' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.filter(u => u.is_archived).map((u) => {
                      const archivedDate = u.archived_at ? new Date(u.archived_at) : null;
                      const autoDeleteDate = archivedDate ? new Date(archivedDate.getTime() + (365 * 7 * 24 * 60 * 60 * 1000)) : null;
                      const canAutoDelete = autoDeleteDate && autoDeleteDate < new Date();
                      
                      return (
                        <tr key={u.id} className="border-b border-gray-800 hover:bg-gray-800/50">
                          <td className="py-3 px-4" style={{ color: '#445277' }}>{u.full_name}</td>
                          <td className="py-3 px-4" style={{ color: '#331a67' }}>{u.email}</td>
                          <td className="py-3 px-4 text-gray-400">
                            {archivedDate ? archivedDate.toLocaleDateString() : 'Unknown'}
                          </td>
                          <td className="py-3 px-4">
                            <span className="text-xs px-2 py-1 bg-orange-100 text-orange-800 rounded">
                              {u.archive_reason || 'Unknown'}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            <div className="text-sm">
                              {autoDeleteDate ? (
                                <>
                                  <div style={{ color: canAutoDelete ? '#ef4444' : '#331a67' }}>
                                    {autoDeleteDate.toLocaleDateString()}
                                  </div>
                                  {canAutoDelete && (
                                    <div className="text-xs text-red-600">Ready for deletion</div>
                                  )}
                                </>
                              ) : 'Unknown'}
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex flex-wrap gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setSelectedUser(u);
                                  setUserDetails(null);
                                  fetchUserPayments(u.id);
                                  fetchUserAcceptances(u.id);
                                  fetchUserDetails(u.id);
                                }}
                                className="text-xs"
                              >
                                View
                              </Button>
                              <Button
                                size="sm"
                                className="bg-green-600 hover:bg-green-700 text-white text-xs"
                                onClick={() => handleUnarchiveUser(u.id)}
                                title="Restore user to active status"
                              >
                                Unarchive
                              </Button>
                              <Button
                                size="sm"
                                className="bg-red-600 hover:bg-red-700 text-white text-xs"
                                onClick={() => handleDeleteUser(u.id, u.username)}
                                title="Permanently delete - preserves payment/legal records"
                              >
                                Delete
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        )}
      </Card>

      {/* Payments History (paginated, 50/page) */}
      <Card data-testid="admin-payments-history-card">
        <CardHeader>
          <button
            type="button"
            onClick={() => setShowPaymentsHistory((v) => !v)}
            className="w-full flex items-center justify-between hover:opacity-80 transition-opacity"
            data-testid="admin-payments-toggle"
          >
            <CardTitle className="flex items-center gap-2 font-bold" style={{ color: '#331a67' }}>
              <CreditCard className="w-5 h-5" />
              Payments History ({paymentsTotal})
            </CardTitle>
            <span className="text-sm text-gray-600">{showPaymentsHistory ? 'Hide' : 'Show'}</span>
          </button>
        </CardHeader>
        {showPaymentsHistory && (
          <CardContent>
            {payments.length === 0 ? (
              <p className="text-gray-500 text-center py-6">No payments to display.</p>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-100 text-gray-700">
                      <tr>
                        <th className="text-left p-2">Date</th>
                        <th className="text-left p-2">User</th>
                        <th className="text-left p-2">Amount</th>
                        <th className="text-left p-2">Type</th>
                        <th className="text-left p-2">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {payments.map((p) => (
                        <tr key={p.id} className="border-b border-gray-200" data-testid={`admin-payment-row-${p.id}`}>
                          <td className="p-2 text-gray-800">{p.created_at ? new Date(p.created_at).toLocaleString() : '—'}</td>
                          <td className="p-2 text-gray-800">{p.user_email || p.user_id || '—'}</td>
                          <td className="p-2 text-gray-900 font-medium">${((p.amount || 0) / 100).toFixed(2)}</td>
                          <td className="p-2 text-gray-700">{p.payment_type || p.type || '—'}</td>
                          <td className="p-2">
                            <Badge className="bg-cyan-100 text-cyan-800">{p.status || 'unknown'}</Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <Pagination
                  page={paymentsPage}
                  totalPages={paymentsTotalPages}
                  onChange={setPaymentsPage}
                  testidPrefix="admin-payments-pagination"
                  theme="light"
                />
              </>
            )}
          </CardContent>
        )}
      </Card>


      <Card data-testid="admin-drip-purchases-card">
        <CardHeader>
          <button
            type="button"
            onClick={() => setShowDripPurchases((v) => !v)}
            className="w-full flex items-center justify-between hover:opacity-80 transition-opacity"
            data-testid="admin-drip-purchases-toggle"
          >
            <CardTitle className="flex items-center gap-2 font-bold" style={{ color: '#331a67' }}>
              <Zap className="w-5 h-5" />
              Drip Removal Purchases ({dripTotal})
            </CardTitle>
            <span className="text-sm text-gray-600">{showDripPurchases ? 'Hide' : 'Show'}</span>
          </button>
        </CardHeader>
        {showDripPurchases && (
          <CardContent>
            <p className="text-sm text-gray-600 mb-3" data-testid="drip-purchases-revenue">
              {dripTotal} purchase{dripTotal !== 1 ? 's' : ''} · ${(dripRevenue / 100).toFixed(2)} collected
            </p>
            {dripPurchases.length === 0 ? (
              <p className="text-gray-500 text-center py-6" data-testid="no-drip-purchases">
                Nobody has bought drip removal yet.
              </p>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-100 text-gray-700">
                      <tr>
                        <th className="text-left p-2">Date</th>
                        <th className="text-left p-2">Member</th>
                        <th className="text-left p-2">Tier</th>
                        <th className="text-left p-2">Paid</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dripPurchases.map((p) => (
                        <tr key={p.id} className="border-b border-gray-200" data-testid={`drip-purchase-row-${p.id}`}>
                          <td className="p-2 text-gray-800">
                            {p.created_at ? new Date(p.created_at).toLocaleString() : '—'}
                          </td>
                          <td className="p-2 text-gray-800">{p.user_name || p.user_email || p.user_id}</td>
                          <td className="p-2 text-gray-700 capitalize">{p.tier || '—'}</td>
                          <td className="p-2 text-gray-900 font-medium">${((p.amount || 0) / 100).toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <Pagination
                  page={dripPage}
                  totalPages={dripTotalPages}
                  onChange={setDripPage}
                  testidPrefix="admin-drip-pagination"
                  theme="light"
                />
              </>
            )}
          </CardContent>
        )}
      </Card>


      {/* User Details Modal */}
      {selectedUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 overflow-auto">
          <Card className="w-full max-w-3xl bg-white border-gray-300 max-h-[90vh] overflow-y-auto" data-testid="member-details-modal">
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle className="text-black">{selectedUser.full_name}</CardTitle>
                <Button variant="ghost" onClick={() => { setSelectedUser(null); setUserDetails(null); }}>
                  X
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-700">Email</Label>
                  <p className="text-black">{selectedUser.email}</p>
                </div>
                <div>
                  <Label className="text-gray-700">Username</Label>
                  <p className="text-black">{selectedUser.username}</p>
                </div>
                <div>
                  <Label className="text-gray-700">Membership Type</Label>
                  <p className="text-black">{selectedUser.membership_type}</p>
                </div>
                <div>
                  <Label className="text-gray-700">Payment Status</Label>
                  <p className="text-black">{selectedUser.payment_status}</p>
                </div>
              </div>

              {/* Payment History */}
              {userPayments && (
                <div className="mt-4">
                  <h3 className="text-lg font-semibold text-black mb-3">Payment History</h3>
                  {userPayments.payments.length > 0 ? (
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {userPayments.payments.map((payment) => (
                        <div key={payment.id} className="border border-gray-300 rounded p-3">
                          <div className="flex justify-between">
                            <span className="text-gray-900 font-medium">${(payment.amount / 100).toFixed(2)}</span>
                            <span className="text-gray-600">{new Date(payment.created_at).toLocaleDateString()}</span>
                          </div>
                          {payment.label && (
                            <p className="text-sm text-gray-700 mt-0.5">{payment.label}</p>
                          )}
                          <Badge className="mt-1">{payment.status}</Badge>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-600">No payments yet</p>
                  )}
                </div>
              )}

              {/* Legal Acceptances */}
              {userAcceptances && (
                <div className="mt-4">
                  <h3 className="text-lg font-semibold text-black mb-3">Legal Document Acceptances</h3>
                  {userAcceptances.length > 0 ? (
                    <div className="space-y-2">
                      {userAcceptances.map((acceptance) => {
                        const labels = {
                          terms: 'Terms of Service',
                          privacy: 'Privacy Policy',
                          community_guidelines: 'Community Guidelines',
                          marketplace_terms: 'Marketplace Terms',
                          vendor_terms: 'Vendor Terms of Service',
                          marketplace_guidelines: 'Marketplace Guidelines',
                          practitioner_agreement: 'Practitioner / Expert / Contributor Agreement',
                          onboarding_checklist: 'Onboarding Agreement Checklist',
                        };
                        const label = labels[acceptance.document_type] || acceptance.document_type;
                        return (
                          <div key={acceptance.id} className="border border-green-700 rounded p-2 bg-green-50 flex justify-between items-center" data-testid={`legal-acceptance-${acceptance.document_type}`}>
                            <div>
                              <span className="text-green-800 font-semibold text-sm">{label}</span>
                              <div className="text-xs text-gray-600">
                                v{acceptance.document_version} | {new Date(acceptance.accepted_at).toLocaleString()}
                              </div>
                            </div>
                            <span className="text-green-600 font-bold">Accepted</span>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-gray-600">No acceptance records</p>
                  )}
                </div>
              )}

              {/* Enhanced Details */}
              {detailsLoading && (
                <div className="text-center py-4">
                  <span className="text-gray-500">Loading detailed info...</span>
                </div>
              )}

              {userDetails && (
                <>
                  {/* Workshop Purchases */}
                  <div className="mt-4">
                    <h3 className="text-lg font-semibold text-black mb-2">Workshop Purchases ({userDetails.workshop_purchases.length})</h3>
                    {userDetails.workshop_purchases.length > 0 ? (
                      <div className="space-y-1 max-h-32 overflow-y-auto">
                        {userDetails.workshop_purchases.map((wp) => (
                          <div key={wp.id} className="border border-purple-200 rounded p-2 bg-purple-50 flex justify-between text-sm">
                            <span className="text-purple-800 font-medium">{wp.workshop_title}</span>
                            <span className="text-gray-600">${(wp.amount_paid || wp.amount || 0).toFixed(2)} — {new Date(wp.purchase_date || wp.purchased_at || '').toLocaleDateString()}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-sm">No workshop purchases</p>
                    )}
                  </div>

                  {/* Stream Purchases */}
                  <div className="mt-4">
                    <h3 className="text-lg font-semibold text-black mb-2">Stream Purchases ({userDetails.stream_purchases.length})</h3>
                    {userDetails.stream_purchases.length > 0 ? (
                      <div className="space-y-1 max-h-32 overflow-y-auto">
                        {userDetails.stream_purchases.map((sp) => (
                          <div key={sp.id} className="border border-cyan-200 rounded p-2 bg-cyan-50 flex justify-between text-sm">
                            <span className="text-cyan-800 font-medium">{sp.stream_title}</span>
                            <span className="text-gray-600">${(sp.amount_paid || 0).toFixed(2)} — {sp.purchase_type}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-sm">No stream purchases</p>
                    )}
                  </div>

                  {/* Vendor / Storefront */}
                  <div className="mt-4">
                    <h3 className="text-lg font-semibold text-black mb-2">Vendor / Storefronts ({userDetails.vendors.length})</h3>
                    {userDetails.vendors.length > 0 ? (
                      <div className="space-y-1">
                        {userDetails.vendors.map((v) => (
                          <div key={v.id || v.store_slug} className="border border-emerald-200 rounded p-2 bg-emerald-50 flex justify-between text-sm">
                            <span className="text-emerald-800 font-medium">{v.store_name}</span>
                            <Badge className={v.status === 'approved' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}>{v.status}</Badge>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-sm">No storefronts</p>
                    )}
                  </div>

                  {/* Booking Activity */}
                  <div className="mt-4">
                    <h3 className="text-lg font-semibold text-black mb-2">Booking Activity ({userDetails.bookings.length})</h3>
                    {userDetails.bookings.length > 0 ? (
                      <div className="space-y-1 max-h-32 overflow-y-auto">
                        {userDetails.bookings.slice(0, 10).map((b) => (
                          <div key={b.id} className="border border-blue-200 rounded p-2 bg-blue-50 flex justify-between text-sm">
                            <span className="text-blue-800">{b.session_type_name || 'Session'} — {b.status}</span>
                            <span className="text-gray-600">{new Date(b.booking_date || b.created_at || '').toLocaleDateString()}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-sm">No bookings</p>
                    )}
                  </div>

                  {/* Forum Activity */}
                  <div className="mt-4">
                    <h3 className="text-lg font-semibold text-black mb-2">Forum Activity</h3>
                    <div className="flex gap-4">
                      <div className="border border-gray-200 rounded p-2 bg-gray-50 text-sm flex-1 text-center">
                        <div className="text-2xl font-bold text-gray-800">{userDetails.forum.post_count}</div>
                        <div className="text-gray-500">Posts</div>
                      </div>
                      <div className={`border rounded p-2 text-sm flex-1 text-center ${userDetails.forum.reports.length > 0 ? 'border-red-200 bg-red-50' : 'border-gray-200 bg-gray-50'}`}>
                        <div className={`text-2xl font-bold ${userDetails.forum.reports.length > 0 ? 'text-red-600' : 'text-gray-800'}`}>{userDetails.forum.reports.length}</div>
                        <div className="text-gray-500">Reports Against</div>
                      </div>
                    </div>
                  </div>

                  {/* Practitioner / Expert / Contributor Agreements */}
                  <div className="mt-4">
                    <h3 className="text-lg font-semibold text-black mb-2">
                      Practitioner Agreements ({userDetails.practitioner_agreements.length})
                    </h3>
                    {userDetails.practitioner_agreements.length > 0 ? (
                      <div className="space-y-1">
                        {userDetails.practitioner_agreements.map((a) => {
                          const formLabels = {
                            practitioner_agreement: 'Practitioner / Expert / Contributor Agreement',
                            onboarding_agreement: 'Onboarding Agreement (legacy)',
                            onboarding_checklist: 'Onboarding Agreement Checklist',
                            vendor_agreement: 'Marketplace Vendor Agreement (legacy)',
                          };
                          const label = formLabels[a.form_type] || a.form_type || 'Onboarding Agreement';
                          return (
                            <div
                              key={a.id}
                              className="border border-teal-200 rounded p-2 bg-teal-50 text-sm"
                              data-testid={`practitioner-agreement-${a.form_type || 'unknown'}`}
                            >
                              <div className="flex justify-between items-start gap-2">
                                <div className="flex-1">
                                  <div className="text-teal-800 font-medium">{label}</div>
                                  <div className="text-xs text-gray-600">
                                    {a.document_version && <>v{a.document_version} · </>}
                                    Signed: {a.signed_at ? new Date(a.signed_at).toLocaleString() : '—'}
                                  </div>
                                  {a.signature_name && (
                                    <div className="text-xs italic text-gray-700 mt-0.5">
                                      Signature: {a.signature_name}
                                    </div>
                                  )}
                                </div>
                                <span className="text-green-600 font-bold whitespace-nowrap">Signed</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-gray-600 text-sm">No practitioner agreements signed yet</p>
                    )}
                  </div>
                </>
              )}

              {/* Admin Actions */}
              <div className="mt-6 pt-6 border-t border-gray-300">
                <h3 className="text-lg font-semibold text-black mb-3">Admin Actions</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-700 mb-2 block">Grant Membership</Label>
                    <div className="flex flex-col gap-2">
                      <Button
                        size="sm"
                        onClick={() => handleGrantFreeMembership(selectedUser.id)}
                        className="bg-purple-500 hover:bg-purple-600"
                      >
                        Free (Comp)
                      </Button>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={() => handleGrantMembership(selectedUser.id, 'monthly', 30)}
                          className="flex-1"
                        >
                          1 Month
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => handleGrantMembership(selectedUser.id, 'yearly', 365)}
                          className="flex-1"
                        >
                          1 Year
                        </Button>
                      </div>
                    </div>
                  </div>
                  <div>
                    <Label className="text-gray-700 mb-2 block">Admin Role</Label>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleToggleAdmin(selectedUser.id, selectedUser.role)}
                      className="w-full"
                      disabled={selectedUser.id === user.id}
                    >
                      {selectedUser.role === 'admin' ? 'Remove Admin' : 'Make Admin'}
                    </Button>
                  </div>
                  <div>
                    <Label className="text-gray-700 mb-2 block">Practitioner Forms</Label>
                    <Button
                      size="sm"
                      onClick={async () => {
                        try {
                          const token = localStorage.getItem('token');
                          await axios.post(`${API}/practitioner-forms/admin/send-form`, {
                            user_id: selectedUser.id,
                            form_type: 'onboarding_agreement',
                          }, { headers: { Authorization: `Bearer ${token}` } });
                          toast.success('Onboarding form sent!');
                        } catch (err) {
                          toast.error(err.response?.data?.detail || 'Failed to send form');
                        }
                      }}
                      className="bg-teal-500 hover:bg-teal-600 text-white w-full"
                      data-testid="send-onboarding-form-btn"
                    >
                      Send Onboarding Form
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <MemberPlanModal
        userId={planMember?.id}
        memberName={planMember?.full_name || planMember?.email}
        open={!!planMember}
        onClose={() => setPlanMember(null)}
      />

      {changingEmail && (
        <ChangeEmailDialog
          account={changingEmail}
          onClose={() => setChangingEmail(null)}
          onDone={fetchUsers}
        />
      )}
      {removingAccount && (
        <RemoveAccountDialog
          account={removingAccount}
          onClose={() => setRemovingAccount(null)}
          onDone={fetchUsers}
        />
      )}
    </div>
  );
};

export default AdminMembershipManagement;
